/**
 * Business Schema Generator - Based on FINAL-SCHEMA-99-FIXED.json structure
 * Generates proper JSON-LD schema for local business pages
 */

import { getSiteBaseUrl } from '../config/site';

/**
 * Generate complete business schema graph
 * @param {Object} data - Business data from template
 * @returns {Object|null} - Schema graph or null if invalid data
 */
export const generateBusinessSchemaGraph = (data) => {
  if (!data || !data.name || data.name.trim().length < 2) {
    return null;
  }

  // Always use production URL for schema (not localhost)
  const baseUrl = getSiteBaseUrl();
  const categoryPath = data.categorySlug ? `/${data.categorySlug}` : '';
  const businessUrl = data.url || `${baseUrl}/${data.stateSlug || 'state'}/${data.citySlug || 'city'}${categoryPath}/${data.slug || ''}`;
  const businessId = `${businessUrl}#business`;

  const graph = [];

  // 1. Main LocalBusiness Schema
  const localBusiness = {
    '@type': data.businessTypes || ['LocalBusiness'],
    '@id': businessId,
    name: data.name,
    description: data.description || undefined,
    url: businessUrl,
    telephone: data.phone || undefined,
    email: data.email || undefined,
    priceRange: data.priceRange || '$$'
  };

  // Alternate name
  if (data.alternateName) {
    localBusiness.alternateName = data.alternateName;
  }

  // Address
  if (data.streetAddress) {
    localBusiness.address = {
      '@type': 'PostalAddress',
      streetAddress: data.streetAddress,
      addressLocality: data.city || undefined,
      addressRegion: data.state || undefined,
      postalCode: data.postalCode || undefined,
      addressCountry: 'IN'
    };
  }

  // Geo coordinates
  const lat = parseFloat(data.latitude);
  const lng = parseFloat(data.longitude);
  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
    localBusiness.geo = {
      '@type': 'GeoCoordinates',
      latitude: lat,
      longitude: lng
    };
    localBusiness.hasMap = `https://www.google.com/maps?q=${lat},${lng}`;

    // Add Plus Code (Google Open Location Code) if available
    if (data.plusCode) {
      localBusiness.geo.plusCode = data.plusCode;
    }
  }

  // Opening Hours
  if (data.openingHours && data.openingHours.length > 0) {
    localBusiness.openingHoursSpecification = data.openingHours.map(h => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: h.days || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: h.opens || '09:00',
      closes: h.closes || '18:00'
    }));
  }

  // Images - only valid HTTP URLs
  const validImages = (data.images || []).filter(img => 
    img && typeof img === 'string' && img.startsWith('http')
  );
  if (validImages.length > 0) {
    localBusiness.image = validImages;
  }

  // Aggregate Rating
  if (data.rating && data.reviewCount) {
    localBusiness.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(data.rating),
      reviewCount: String(data.reviewCount),
      bestRating: '5',
      worstRating: '1'
    };
  }

  // Services as makesOffer
  const validServices = (data.services || []).filter(s => s.name && s.name.trim());
  if (validServices.length > 0) {
    localBusiness.makesOffer = validServices.map(service => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description || undefined,
        serviceType: service.name
      }
    }));
  }

  // Area Served
  if (data.city) {
    localBusiness.areaServed = {
      '@type': 'City',
      name: data.city,
      containedInPlace: data.state ? {
        '@type': 'State',
        name: data.state,
        containedInPlace: {
          '@type': 'Country',
          name: 'India'
        }
      } : undefined
    };
  }

  // Payment accepted
  if (data.paymentAccepted) {
    localBusiness.paymentAccepted = Array.isArray(data.paymentAccepted) 
      ? data.paymentAccepted.join(', ') 
      : data.paymentAccepted;
    localBusiness.currenciesAccepted = 'INR';
  }

  // Keywords/knowsAbout
  if (data.keywords && data.keywords.length > 0) {
    localBusiness.knowsAbout = data.keywords;
  }

  // Social profiles
  const validSocial = (data.socialProfiles || []).filter(url => 
    url && typeof url === 'string' && url.startsWith('http')
  );
  if (validSocial.length > 0) {
    localBusiness.sameAs = validSocial;
  }

  // Clean undefined values
  Object.keys(localBusiness).forEach(key => {
    if (localBusiness[key] === undefined) {
      delete localBusiness[key];
    }
  });

  graph.push(localBusiness);

  // 2. Store Schema (linked to business)
  if (data.streetAddress) {
    graph.push({
      '@type': 'Store',
      '@id': `${businessUrl}#store`,
      name: data.alternateName || data.name,
      branchOf: { '@id': businessId },
      address: {
        '@type': 'PostalAddress',
        streetAddress: data.streetAddress,
        addressLocality: data.city || undefined,
        addressRegion: data.state || undefined,
        postalCode: data.postalCode || undefined,
        addressCountry: 'IN'
      }
    });
  }

  // 3. Individual Review Schemas
  const validReviews = (data.reviews || []).filter(r => 
    r && r.text && r.text.trim().length > 5 && r.authorName
  );
  
  validReviews.slice(0, 10).forEach((review, index) => {
    graph.push({
      '@type': 'Review',
      '@id': `${businessUrl}#review${index + 1}`,
      itemReviewed: { '@id': businessId },
      author: {
        '@type': 'Person',
        name: review.authorName
      },
      datePublished: review.date || new Date().toISOString().split('T')[0],
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating || 5,
        bestRating: 5,
        worstRating: 1
      },
      reviewBody: review.text
    });
  });

  // 4. WebPage Schema
  graph.push({
    '@type': 'WebPage',
    '@id': `${businessUrl}#webpage`,
    url: businessUrl,
    name: `${data.name} - ${data.city || ''}`,
    description: data.description || `${data.name} in ${data.city || 'your area'}`,
    inLanguage: 'en-IN',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${baseUrl}/#website`,
      name: 'Visibeen Directory',
      url: baseUrl
    },
    about: { '@id': businessId },
    breadcrumb: { '@id': `${businessUrl}#breadcrumb` }
  });

  // 5. BreadcrumbList Schema
  const breadcrumbs = [
    { position: 1, name: 'Home', item: baseUrl }
  ];

  if (data.state && data.stateSlug) {
    breadcrumbs.push({
      position: 2,
      name: data.state,
      item: `${baseUrl}/in/${data.stateSlug}`
    });
  }

  if (data.city && data.citySlug) {
    breadcrumbs.push({
      position: 3,
      name: data.city,
      item: `${baseUrl}/in/${data.stateSlug}/${data.citySlug}`
    });
  }

  if (data.category && data.categorySlug) {
    breadcrumbs.push({
      position: breadcrumbs.length + 1,
      name: data.category,
      item: `${baseUrl}/in/${data.stateSlug}/${data.citySlug}/${data.categorySlug}`
    });
  }

  breadcrumbs.push({
    position: breadcrumbs.length + 1,
    name: data.alternateName || data.name,
    item: businessUrl
  });

  graph.push({
    '@type': 'BreadcrumbList',
    '@id': `${businessUrl}#breadcrumb`,
    itemListElement: breadcrumbs.map(b => ({
      '@type': 'ListItem',
      position: b.position,
      name: b.name,
      item: b.item
    }))
  });

  // 6. ItemList Schema for Featured Products/Services (like Reliance Digital)
  if (validServices.length > 0) {
    graph.push({
      '@type': 'ItemList',
      '@id': `${businessUrl}#services`,
      name: `${data.name} Services`,
      itemListElement: validServices.slice(0, 10).map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Service',
          name: service.name,
          description: service.description || undefined,
          provider: { '@id': businessId }
        }
      }))
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph
  };
};

export default generateBusinessSchemaGraph;
